/**
 * El chat de reescritura (Agentes II, 17-sep-2026).
 *
 * Rais: «en el chat de reescritura sale iluminado solo el editor; deberían estar todos los
 * implicados: al menos el ghostwriter, y quizás el coach». Aquí hablas con el EDITOR DE DESARROLLO,
 * y el editor encarga al escritor fantasma, al coach o al corrector cuando hace falta. Sus respuestas
 * salen en el mismo hilo, cada una con su cara y su nombre, y con lo que el editor les pidió.
 *
 * El manuscrito abierto viaja como contexto, igual que con el coach: sin él, el editor opinaría de
 * un libro que no ha leído.
 */
import { useEffect, useRef, useState } from "react";
import TextoChat from "./TextoChat";
import { PropuestaEncargo } from "./PropuestaEncargo";
import { frasesDeConsultas } from "../logica";
import { useUser } from "@clerk/react";
import Cara from "./Cara";
import { SinSuscripcion, hiloReescrituraConEstilo, olvidarReescritura, reescribirEnEquipo } from "../api";
import type { TurnoReescritura } from "../api";
import { inicialesDe, nombreDeRuta } from "../logica";

const NOMBRE: Record<string, string> = {
  editor: "Editor de desarrollo",
  ghostwriter: "Escritor fantasma",
  coach: "Coach literario",
  copyeditor: "Corrector",
};

export default function Reescritura({ carpeta, doc, alGastar, alSinSuscripcion, alHablar }: {
  carpeta: string;
  doc: string | null;
  alGastar: () => void;
  alSinSuscripcion: () => void;
  alHablar?: (agente: string, encargo: string) => void;
}) {
  const [hilo, setHilo] = useState<TurnoReescritura[]>([]);
  const [cargando, setCargando] = useState(true);
  const [texto, setTexto] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const [libroEntero, setLibroEntero] = useState(false);   // R-18
  const [faltanEstilo, setFaltanEstilo] = useState<string[] | null>(null);   // T-29
  const { user } = useUser();
  const yo = inicialesDe({ firstName: user?.firstName, lastName: user?.lastName, email: user?.primaryEmailAddress?.emailAddress });
  const pie = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!carpeta) { setCargando(false); return; }
    let vigente = true;
    setCargando(true);
    hiloReescrituraConEstilo(carpeta)
      .then((d) => { if (vigente) { setHilo(d.hilo); setFaltanEstilo(d.faltanEstilo); } })
      .catch((e) => { if (vigente) setFallo(/404/.test(String(e?.message)) ? "El chat de reescritura necesita el motor 1.6.2. Actualízalo desde Ajustes." : "No se ha podido leer la conversación guardada."); })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [carpeta]);

  useEffect(() => { pie.current?.scrollIntoView({ block: "end" }); }, [hilo.length, ocupado]);

  async function enviar() {
    const mio = texto.trim();
    if (!mio || ocupado) return;
    if (!carpeta) { setFallo("Abre antes un proyecto en Proyectos."); return; }
    const turno = hilo.length;
    setHilo((h) => [...h, { role: "user", content: mio, at: Date.now() }]);
    setTexto(""); setOcupado(true); setFallo(null);
    try {
      const nuevos = await reescribirEnEquipo(carpeta, mio, doc ? [doc] : [], turno, null, libroEntero, doc ?? null);
      setHilo((h) => [...h, ...nuevos]);
      alGastar();
    } catch (e) {
      setHilo((h) => h.slice(0, -1));
      setTexto(mio);
      if (e instanceof SinSuscripcion) alSinSuscripcion();
      else setFallo(e instanceof Error ? e.message : "No se ha podido enviar.");
    } finally {
      setOcupado(false);
    }
  }

  async function borrar() {
    try { await olvidarReescritura(carpeta); setHilo([]); } catch { setFallo("No se ha podido borrar la conversación."); }
  }

  return (
    <>
      <section className="carta hilo">
        <div className="quien-hilo">
          <span className="caras-juntas grande">
            {["editor", "ghostwriter", "coach"].map((id) => <Cara key={id} id={id} nombre={NOMBRE[id]} />)}
          </span>
          <span className="n">
            <b>Chat de reescritura</b>
            <span>Hablas con el editor; él encarga al escritor fantasma, al coach o al corrector</span>
          </span>
          {hilo.length > 0 && <button className="chip" onClick={borrar} title="Borra el hilo guardado en tu carpeta">Borrar la conversación</button>}
        </div>

        {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
        {!!faltanEstilo?.length && (
          <div className="aviso">
            <span>
              Al editor le falta {faltanEstilo.map((f) => f.replace("la conversación del autor", "tu conversación")).join(", ").replace(/, ([^,]*)$/, " y $1")}. Sin eso no toca tu voz
              (ritmo, adjetivos, puntuación): se queda en estructura, trama y personajes.
            </span>
            {alHablar && (
              <button className="btn fantasma" onClick={() => alHablar("coach",
                "Quiero que el editor respete mi voz al reescribir. Ayúdame a dejar claro mi estilo: qué rasgos tiene, qué funciona y qué no hay que tocar.")}>
                Trabajar el estilo con el coach
              </button>
            )}
          </div>
        )}
        {cargando && <p className="vacio">Abriendo lo que ya hablasteis…</p>}
        {!cargando && !hilo.length && !fallo && (
          <p className="vacio">
            Cuéntale al editor qué quieres reescribir. Él propone el plan y, si hace falta escribir o
            revisar el estilo, se lo encarga al escritor fantasma o al coach en el mismo momento.
            Cada encargo es una llamada más con tu clave (como mucho dos por mensaje). Cuando entregan, el
            editor te presenta lo que han hecho y el siguiente paso: otra llamada, corta y sin el libro.
          </p>
        )}

        <div className="relevo">
          {hilo.map((t, i) => {
            const quien = t.role === "user" ? null : (t.quien ?? "editor");
            return (
              <div className={"paso turno" + (quien ? "" : " mio")} key={i}>
                {quien
                  ? <Cara id={quien} nombre={NOMBRE[quien] ?? quien} />
                  : <span className="cara" style={{ background: "var(--azul-suave)", color: "var(--azul)" }}>{yo}</span>}
                <span className="qu">
                  <b>{quien ? NOMBRE[quien] ?? quien : "Tú"}</b>
                  {t.encargo && <span className="meta">Encargo del editor: {t.encargo}</span>}
                  {t.cierre && <span className="meta">Lo que ha hecho el equipo, y qué te propongo</span>}
                  {quien
                    ? <TextoChat texto={t.content} style={t.fallo ? { color: "var(--mut2)" } : undefined} />
                    : <span className="texto-chat">{t.content}</span>}
                  {quien && t.propuestas?.map((p) => (
                    <PropuestaEncargo key={p.id} p={p} agente="reescritura" carpeta={carpeta} doc={doc}
                                      alCambiar={(nueva) => setHilo((h) => h.map((x) => (x.propuestas?.some((y) => y.id === nueva.id)
                                        ? { ...x, propuestas: x.propuestas.map((y) => (y.id === nueva.id ? nueva : y)) } : x)))}
                                      alLanzar={alGastar} />
                  ))}
                  {quien && frasesDeConsultas(t.consultas) && <span className="consultas">{frasesDeConsultas(t.consultas)}</span>}
                  {!!t.encarga?.length && (
                    <span className="meta">→ Encarga: {t.encarga.map((a) => NOMBRE[a] ?? a).join(" · ")}</span>
                  )}
                </span>
              </div>
            );
          })}
          {ocupado && (
            <div className="paso">
              <span className="cara" style={{ background: "var(--sup3)", color: "var(--mut)" }}>··</span>
              <span className="qu"><b>Editor de desarrollo</b><span>Trabajando… si encarga algo, tardará un poco más.</span></span>
            </div>
          )}
          <div ref={pie} />
        </div>
      </section>

      <section className="carta">
        <div className="formulario" style={{ maxWidth: "none" }}>
          <div className="dir">
            <textarea className="escribir" rows={3} style={{ resize: "vertical" }}
                      placeholder="Qué quieres reescribir, y por qué"
                      value={texto} onChange={(e) => setTexto(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) enviar(); }} />
          </div>
          <div className="pie-form">
            <p className="vacio">
              {doc ? <>Va adjunto <b>{nombreDeRuta(doc)}</b>{libroEntero ? " entero" : ", sus primeras 1.450 palabras"}.</> : "Sin manuscrito elegido: el editor solo verá lo que escribas."} ⌘↵ para enviar.
              {doc && <> <button className={"chip" + (libroEntero ? " puesto" : "")} onClick={() => setLibroEntero(!libroEntero)}>{libroEntero ? "✓ " : ""}Libro entero</button></>}
            </p>
            <button className="btn" onClick={enviar} disabled={!texto.trim() || ocupado}>{ocupado ? "Trabajando…" : "Enviar"}</button>
          </div>
        </div>
      </section>
    </>
  );
}
